/**
 * SlidePanel — form-agnostic overlay panel shell.
 *
 * Renders a fixed right-side (or left-side) panel over an overlay backdrop.
 * Handles:
 *  - Slide-in / slide-out animation via CSS classes (no JS animation library)
 *  - Scroll lock on the page body while open
 *  - ESC key to close
 *  - Click-outside (overlay) to close (configurable)
 *  - Focus management: moves focus into panel on open, restores on close
 *  - Tab-key trap within the panel
 *  - Dirty-state close guard (shows DiscardChangesDialog before closing)
 *  - aria-modal / role="dialog" accessibility attributes
 *  - Error containment: the body is wrapped in an error boundary so a throw in
 *    the panel's content renders the retry fallback *inside* the panel instead
 *    of tearing the whole panel down and leaving the fallback on the bare page.
 *    While that fallback is up the footer actions are withheld — Save would
 *    otherwise stay clickable over a body that never finished rendering.
 */
import { X } from "lucide-react";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LazyComponentBoundary } from "@/components/LazyLoadErrorFallback/LazyComponentBoundary";
import { Loading } from "@/components/Loading";
import ReactPortal from "@/components/ReactPortal";
import DiscardChangesDialog from "./DiscardChangesDialog";
import { SlidePanelProps } from "./types";
import { useFocusTrap } from "./useFocusTrap";
import { usePanelTransition } from "./usePanelTransition";
import { useResizableWidth } from "./useResizableWidth";
import "./slide-panel.scss";
import "./panel-footer-actions.scss";

const SlidePanel: React.FC<SlidePanelProps> = ({
  open,
  onClose,
  title,
  subtitle,
  size = "md",
  side = "right",
  className = "",
  isDirty = false,
  disableCloseOnOverlayClick = false,
  children,
  headerActions,
  summaryContent,
  footerActions,
  errorBanner,
  isLoading = false,
  resizable = false,
  defaultWidthVw = 85,
  minWidthVw = 85,
  maxWidthVw = 100,
  renderResizeEdge,
  testId,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  // The body threw and the boundary is showing its retry fallback. The footer
  // is hidden while that is true: its actions (Save, Continue) act on state the
  // failed body owns, so leaving them live invites a save of half-built values.
  const [bodyFailed, setBodyFailed] = useState(false);
  const handleBodyError = useCallback(() => setBodyFailed(true), []);
  const handleBodyReset = useCallback(() => setBodyFailed(false), []);

  const { mounted, visible } = usePanelTransition(open);

  // Clear the failure once the panel has fully left the screen. `mounted`
  // going false makes this component return null, which unmounts the boundary
  // subtree — so the boundary comes back with a clean error state on reopen,
  // while `bodyFailed` lives on the SlidePanel instance, which callers keep
  // rendered at `open={false}`. Without this reset the flags disagree: the body
  // renders fine on reopen but the footer is gone for good, and there is no
  // Retry button left to fire `onReset`. Keyed on `mounted` rather than `open`
  // so the footer does not pop back mid slide-out.
  useEffect(() => {
    if (!mounted) setBodyFailed(false);
  }, [mounted]);

  const { widthVw, resizeEnabled, startResize, isResizing } = useResizableWidth(
    {
      resizable,
      defaultVw: defaultWidthVw,
      minVw: minWidthVw,
      maxVw: maxWidthVw,
    },
  );

  const requestClose = useCallback(() => {
    if (showDiscardDialog) return;

    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    onClose();
  }, [isDirty, onClose, showDiscardDialog]);

  // Guard ESC so it does not re-trigger when the discard dialog is already open.
  const handleEscape = useCallback(() => {
    requestClose();
  }, [requestClose]);

  useFocusTrap(panelRef, visible, handleEscape);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardDialog(false);
    onClose();
  }, [onClose]);

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardDialog(false);
  }, []);

  const handleOverlayClick = useCallback(() => {
    if (!disableCloseOnOverlayClick) requestClose();
  }, [disableCloseOnOverlayClick, requestClose]);

  if (!mounted) return null;

  const sideClass = side === "left" ? "tlx-panel--left" : "";
  const sizeClass = `tlx-panel--${size}`;
  const openClass = visible ? "tlx-panel--open" : "";
  const overlayVisibleClass = visible ? "tlx-panel-overlay--visible" : "";
  const renderedFooterActions =
    typeof footerActions === "function"
      ? footerActions({ requestClose })
      : footerActions;

  return (
    <ReactPortal wrapperId="slide-panel-portal">
      <div
        className={`tlx-panel-overlay ${overlayVisibleClass}`}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-test-id={testId}
        className={`tlx-panel ${sizeClass} ${sideClass} ${openClass} ${
          resizable ? "tlx-panel--resizable" : ""
        } ${isResizing ? "tlx-panel--resizing" : ""} ${className}`}
        style={resizeEnabled ? { width: `${widthVw}vw` } : undefined}
      >
        {resizeEnabled &&
          (renderResizeEdge ? (
            <div className="tlx-panel-resize-edge">
              {renderResizeEdge({ startResize, isResizing })}
            </div>
          ) : (
            <div
              className="tlx-panel-resize-handle"
              role="separator"
              aria-orientation="vertical"
              aria-label={t("slidePanel.resizePanel")}
              onMouseDown={startResize}
            >
              <span
                className="tlx-panel-resize-handle__grip"
                aria-hidden="true"
              />
            </div>
          ))}
        <div className="tlx-panel-header">
          <div className="tlx-panel-header__left">
            <div className="tlx-panel-header__text">
              <h2 id={titleId} className="tlx-panel-header__title">
                {title}
              </h2>
              {subtitle && (
                <div className="tlx-panel-header__subtitle">{subtitle}</div>
              )}
            </div>
          </div>
          {headerActions && (
            <div className="tlx-panel-header__actions">{headerActions}</div>
          )}
          <button
            type="button"
            className="tlx-panel-header__close-btn"
            onClick={requestClose}
            aria-label={t("slidePanel.closePanel")}
          >
            <X size={18} />
          </button>
        </div>

        {summaryContent && (
          <div className="tlx-panel-summary">{summaryContent}</div>
        )}

        <div className="tlx-panel-body">
          {isLoading ? (
            <Loading isLoading />
          ) : (
            <LazyComponentBoundary
              onError={handleBodyError}
              onReset={handleBodyReset}
            >
              {children}
            </LazyComponentBoundary>
          )}
        </div>

        {errorBanner && (
          <div className="tlx-panel-error-banner" role="alert">
            {errorBanner}
          </div>
        )}

        {!bodyFailed && renderedFooterActions && (
          <div className="tlx-panel-footer">{renderedFooterActions}</div>
        )}
      </div>

      {showDiscardDialog && (
        <DiscardChangesDialog
          open={showDiscardDialog}
          onConfirm={handleDiscardConfirm}
          onCancel={handleDiscardCancel}
        />
      )}
    </ReactPortal>
  );
};

export default SlidePanel;
